import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { getCurrencySymbol } from '../services/currencyUtils';

export const TIER_CURRENCIES = ['ILS', 'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF'];

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** Server cap on a tier volume (CommissionScheduleValidator.MAX_VOLUME). */
export const MAX_VOLUME = 999999999999;

/** True when a finite number has at most 2 decimal places (string-based: avoids float error). */
export const hasAtMost2Decimals = (v) => {
  const s = String(v);
  if (/e/i.test(s)) return Number.isInteger(v);
  const dot = s.indexOf('.');
  return dot === -1 || s.length - dot - 1 <= 2;
};

/** Message for a rate that breaks the server's 0..100, max 2 decimals rule, else null. */
export const rateError = (v) => {
  if (!isNum(v) || v < 0 || v > 100) return 'Rate must be between 0 and 100';
  if (!hasAtMost2Decimals(v)) return 'Rate can have at most 2 decimal places';
  return null;
};

/** Message for a volume above the server cap or with more than 2 decimals, else null. */
const volumeLimitError = (v) => {
  if (v > MAX_VOLUME) return `Must be at most ${MAX_VOLUME}`;
  if (!hasAtMost2Decimals(v)) return 'At most 2 decimal places';
  return null;
};

/**
 * Mirrors the server's schedule rules (CM-02):
 *  - at least one tier and a currency
 *  - the first tier starts at 0
 *  - tiers are contiguous: each starts exactly where the previous one ends
 *  - only the LAST tier is open-ended (maxVolume null), and it must be
 *  - rates are within 0..100 with at most 2 decimal places
 *  - volumes have at most 2 decimal places and are at most 999999999999
 *
 * Values may be `''` while the admin is still typing; that is reported as an error, never
 * coerced. Returns `{ valid, general, currency, rows: [{ min, max, rate }] }`, where each row
 * entry is an error message or null.
 */
export const validateTierTable = ({ tierCurrencyCode, tiers } = {}) => {
  const list = Array.isArray(tiers) ? tiers : [];
  const rows = list.map(() => ({ min: null, max: null, rate: null }));
  const general = [];
  let currency = null;

  if (!tierCurrencyCode) currency = 'Choose a currency';
  if (list.length === 0) general.push('Add at least one tier');

  list.forEach((tier, i) => {
    const last = i === list.length - 1;
    const prev = i > 0 ? list[i - 1] : null;

    if (!isNum(tier.minVolume) || tier.minVolume < 0) {
      rows[i].min = 'Enter a start of 0 or more';
    } else if (volumeLimitError(tier.minVolume)) {
      rows[i].min = volumeLimitError(tier.minVolume);
    } else if (i === 0) {
      if (tier.minVolume !== 0) rows[i].min = 'The first tier must start at 0';
    } else if (prev && isNum(prev.maxVolume)) {
      if (tier.minVolume < prev.maxVolume) {
        rows[i].min = `Overlaps the previous tier, which ends at ${prev.maxVolume}`;
      } else if (tier.minVolume > prev.maxVolume) {
        rows[i].min = `Gap: the previous tier ends at ${prev.maxVolume}`;
      }
    }

    if (last) {
      if (tier.maxVolume !== null) rows[i].max = 'The last tier must be open-ended';
    } else if (tier.maxVolume === null) {
      rows[i].max = 'Only the last tier can be open-ended';
    } else if (!isNum(tier.maxVolume)) {
      rows[i].max = 'Enter an upper bound';
    } else if (volumeLimitError(tier.maxVolume)) {
      rows[i].max = volumeLimitError(tier.maxVolume);
    } else if (isNum(tier.minVolume) && tier.maxVolume <= tier.minVolume) {
      rows[i].max = 'Must be greater than the start';
    }

    rows[i].rate = rateError(tier.ratePercent);
  });

  const valid = !currency
    && general.length === 0
    && rows.every((r) => !r.min && !r.max && !r.rate);
  return { valid, general, currency, rows };
};

export const newTierTable = (tierCurrencyCode = 'ILS') => ({
  tierCurrencyCode,
  tiers: [{ minVolume: 0, maxVolume: null, ratePercent: 20 }],
});

const parseNumber = (raw) => {
  if (raw === '') return '';
  const n = Number(raw);
  return Number.isFinite(n) ? n : '';
};

/**
 * Controlled editor for a volume-tier commission table. Emits `{ tierCurrencyCode, tiers[] }`
 * through `onChange` on every edit (including intermediate, invalid states, because a controlled
 * input must be able to hold them) and reports validity through `onValidityChange` so the parent
 * can keep its Save button disabled until the table passes {@link validateTierTable}.
 */
const TierTableEditor = ({ value, onChange, onValidityChange, disabled = false }) => {
  const tierCurrencyCode = value?.tierCurrencyCode || '';
  const tiers = Array.isArray(value?.tiers) ? value.tiers : [];
  const result = validateTierTable({ tierCurrencyCode, tiers });
  const symbol = tierCurrencyCode ? getCurrencySymbol(tierCurrencyCode) : '';

  const cbRef = useRef(onValidityChange);
  cbRef.current = onValidityChange;
  useEffect(() => {
    if (cbRef.current) cbRef.current(result.valid);
  }, [result.valid]);

  const emit = (nextTiers, nextCurrency = tierCurrencyCode) =>
    onChange({ tierCurrencyCode: nextCurrency, tiers: nextTiers });

  const updateRow = (index, patch) => {
    const next = tiers.map((t, i) => (i === index ? { ...t, ...patch } : t));
    // Editing a tier's upper bound moves the next tier's start with it, so a contiguous table
    // stays contiguous. The start stays editable on its own, which is how gaps/overlaps arise.
    if ('maxVolume' in patch && index < next.length - 1 && isNum(patch.maxVolume)) {
      next[index + 1] = { ...next[index + 1], minVolume: patch.maxVolume };
    }
    emit(next);
  };

  const addRow = () => {
    const prev = tiers[tiers.length - 1];
    const start = prev && isNum(prev.maxVolume) ? prev.maxVolume : '';
    const next = tiers.map((t, i) => (
      // The old last row was open-ended; it now needs a real upper bound.
      i === tiers.length - 1 && t.maxVolume === null ? { ...t, maxVolume: '' } : t
    ));
    next.push({
      minVolume: tiers.length === 0 ? 0 : start,
      maxVolume: null,
      ratePercent: prev && isNum(prev.ratePercent) ? prev.ratePercent : 0,
    });
    emit(next);
  };

  const removeRow = (index) => {
    if (tiers.length <= 1) return;
    const removed = tiers[index];
    const next = tiers.filter((_, i) => i !== index);
    if (index === tiers.length - 1) {
      next[next.length - 1] = { ...next[next.length - 1], maxVolume: null };
    } else if (index === 0) {
      next[0] = { ...next[0], minVolume: 0 };
    } else {
      next[index] = { ...next[index], minVolume: removed.minVolume };
    }
    emit(next);
  };

  return (
    <Box>
      <TextField
        select
        size="small"
        label="Currency"
        value={tierCurrencyCode}
        onChange={(e) => emit(tiers, e.target.value)}
        disabled={disabled}
        error={!!result.currency}
        helperText={result.currency || 'Tier thresholds are monthly sales volume in this currency'}
        sx={{ minWidth: 240, mb: 2 }}
      >
        {[...new Set([...(tierCurrencyCode ? [tierCurrencyCode] : []), ...TIER_CURRENCIES])].map((c) => (
          <MenuItem key={c} value={c}>{c} ({getCurrencySymbol(c)})</MenuItem>
        ))}
      </TextField>

      <Stack spacing={1.5}>
        {tiers.map((tier, i) => {
          const last = i === tiers.length - 1;
          const errs = result.rows[i];
          const n = i + 1;
          return (
            <Stack
              key={i}
              direction="row"
              spacing={1.5}
              alignItems="flex-start"
              data-testid={`tier-row-${i}`}
            >
              <TextField
                size="small"
                type="number"
                label="From"
                inputProps={{ 'aria-label': `Tier ${n} from`, min: 0 }}
                InputProps={symbol ? {
                  startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
                } : undefined}
                value={tier.minVolume}
                onChange={(e) => updateRow(i, { minVolume: parseNumber(e.target.value) })}
                disabled={disabled}
                error={!!errs.min}
                helperText={errs.min || ' '}
                sx={{ width: 170 }}
              />
              <Box sx={{ width: 190 }}>
                <TextField
                  size="small"
                  type="number"
                  label="To"
                  inputProps={{ 'aria-label': `Tier ${n} to`, min: 0 }}
                  InputProps={symbol ? {
                    startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
                  } : undefined}
                  value={tier.maxVolume === null ? '' : tier.maxVolume}
                  placeholder={tier.maxVolume === null ? 'No limit' : ''}
                  onChange={(e) => updateRow(i, { maxVolume: parseNumber(e.target.value) })}
                  disabled={disabled || tier.maxVolume === null}
                  error={!!errs.max}
                  helperText={errs.max || ' '}
                  fullWidth
                />
                {last && (
                  <FormControlLabel
                    sx={{ mt: -1.5, ml: 0 }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={tier.maxVolume === null}
                        disabled={disabled}
                        inputProps={{ 'aria-label': `Tier ${n} has no upper limit` }}
                        onChange={(e) => updateRow(i, { maxVolume: e.target.checked ? null : '' })}
                      />
                    )}
                    label={<Typography variant="caption">No upper limit</Typography>}
                  />
                )}
              </Box>
              <TextField
                size="small"
                type="number"
                label="Rate"
                inputProps={{ 'aria-label': `Tier ${n} rate %`, min: 0, max: 100, step: 'any' }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                value={tier.ratePercent}
                onChange={(e) => updateRow(i, { ratePercent: parseNumber(e.target.value) })}
                disabled={disabled}
                error={!!errs.rate}
                helperText={errs.rate || ' '}
                sx={{ width: 150 }}
              />
              <Tooltip title={tiers.length <= 1 ? 'At least one tier is required' : 'Remove tier'}>
                <span>
                  <IconButton
                    aria-label={`Remove tier ${n}`}
                    onClick={() => removeRow(i)}
                    disabled={disabled || tiers.length <= 1}
                    sx={{ mt: 0.25 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>

      {result.general.map((msg) => (
        <Typography key={msg} variant="body2" color="error" sx={{ mt: 1 }}>{msg}</Typography>
      ))}

      <Button startIcon={<AddIcon />} onClick={addRow} disabled={disabled} sx={{ mt: 1 }}>
        Add tier
      </Button>
    </Box>
  );
};

export default TierTableEditor;
